import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, TrendingUp, CheckCircle2, Clock, BarChart3, Award,
  Building2, Target, Shield, Activity, ArrowLeft, Sparkles, Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { AdminLayout } from "../../components/layout/AdminLayout";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area, RadialBarChart, RadialBar
} from "recharts";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const COLORS = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"];

const TeamAnalyticsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiInsight, setAiInsight] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [statDrill, setStatDrill] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get("/team/analytics");
        setData(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const getAiInsight = async () => {
    if (!data) return;
    setAiLoading(true);
    try {
      const res = await api.post("/ai/insights", {
        context: "team",
        data: {
          team_size: data.team_size,
          completion_rate: data.completion_rate,
          compliance_rate: data.compliance_rate,
          avg_progress: data.avg_progress,
          departments: data.departments,
          top_performers: data.top_performers,
          monthly_trend: data.monthly_trend,
        }
      });
      setAiInsight(res.data.insight);
    } catch (e) { setAiInsight("Unable to generate insight"); }
    finally { setAiLoading(false); }
  };

  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";

  const content = loading ? (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
    </div>
  ) : !data ? (
    <p className="text-center py-20 text-muted-foreground">Failed to load analytics</p>
  ) : (
    <div className="space-y-6" data-testid="team-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1 mb-2 -ml-2">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }} data-testid="team-analytics-title">
            Team Analytics
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Team performance, compliance, and learning trends
          </p>
        </div>
        <Button onClick={getAiInsight} disabled={aiLoading} variant="outline" className="gap-2" data-testid="ai-insight-btn">
          {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-amber-500" />}
          AI Insight
        </Button>
      </div>

      {/* AI Insight Banner */}
      {aiInsight && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-amber-500/30 bg-amber-500/5">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium mb-1 text-amber-700">AI-Powered Insight</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">{aiInsight}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Stats Grid — Clickable */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Team Size", value: data.team_size, sub: `${data.active_users} active`, icon: Users, color: "text-blue-500", bg: "bg-blue-500/10", detail: `${data.active_users} active, ${data.team_size - data.active_users} inactive` },
          { label: "Completion Rate", value: `${data.completion_rate}%`, sub: `${data.completed} completed`, icon: CheckCircle2, color: "text-green-500", bg: "bg-green-500/10", detail: `${data.completed} of ${data.total_enrollments} enrollments completed` },
          { label: "Avg Progress", value: `${data.avg_progress}%`, sub: `${data.in_progress} in progress`, icon: TrendingUp, color: "text-amber-500", bg: "bg-amber-500/10", detail: `${data.in_progress} courses actively being studied, ${data.not_started} not yet started` },
          { label: "Compliance", value: `${data.compliance_rate}%`, sub: "compliance rate", icon: Shield, color: "text-purple-500", bg: "bg-purple-500/10", detail: `Team compliance tracking across all assigned regulations` },
        ].map((st, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Card className="stat-card-3d border-0 shadow-sm" onClick={() => setStatDrill(st)} data-testid={`team-stat-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${st.bg} flex items-center justify-center`}>
                    <st.icon className={`w-5 h-5 ${st.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold leading-none">{st.value}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{st.label}</p>
                    <p className="text-[10px] text-muted-foreground/60">{st.sub}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Monthly Trend — 3D */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="chart-3d border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-primary" /> Monthly Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={data.monthly_trend}>
                  <defs>
                    <linearGradient id="gradCompletedTeam" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradEnrolledTeam" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <filter id="teamShadow"><feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#3b82f6" floodOpacity="0.3" /></filter>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.1)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 12, background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(16px)' }} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="enrolled" stroke="#3b82f6" fill="url(#gradEnrolledTeam)" strokeWidth={3} dot={{ r: 4, fill: "#3b82f6", stroke: "#fff", strokeWidth: 2 }} animationDuration={1500} />
                  <Area type="monotone" dataKey="completed" stroke="#22c55e" fill="url(#gradCompletedTeam)" strokeWidth={3} dot={{ r: 4, fill: "#22c55e", stroke: "#fff", strokeWidth: 2 }} animationDuration={1500} animationBegin={300} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>

        {/* Department Distribution — 3D */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <Card className="chart-3d border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Building2 className="w-4 h-4 text-primary" /> Department Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <defs>
                    <filter id="deptPieShadow"><feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#000" floodOpacity="0.2" /></filter>
                  </defs>
                  <Pie data={data.departments} cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={4}
                    dataKey="count" nameKey="name" animationDuration={1200} animationBegin={200}>
                    {data.departments.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} style={{ filter: 'url(#deptPieShadow)' }} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(255,255,255,0.1)' }} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Enrollment Status — 3D */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="chart-3d border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" /> Enrollment Status</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={[
                  { status: "Completed", count: data.completed, fill: "#22c55e" },
                  { status: "In Progress", count: data.in_progress, fill: "#f59e0b" },
                  { status: "Not Started", count: data.not_started, fill: "#94a3b8" },
                ]}>
                  <defs>
                    <linearGradient id="barGreenTeam" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22c55e" /><stop offset="100%" stopColor="#16a34a" /></linearGradient>
                    <linearGradient id="barAmberTeam" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f59e0b" /><stop offset="100%" stopColor="#d97706" /></linearGradient>
                    <linearGradient id="barSlateTeam" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#94a3b8" /><stop offset="100%" stopColor="#64748b" /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.1)" />
                  <XAxis dataKey="status" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 12, background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(255,255,255,0.1)' }} />
                  <Bar dataKey="count" radius={[10, 10, 0, 0]} animationDuration={1200}>
                    {[
                      { fill: "url(#barGreenTeam)" },
                      { fill: "url(#barAmberTeam)" },
                      { fill: "url(#barSlateTeam)" },
                    ].map((c, i) => <Cell key={i} fill={c.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>

        {/* Top Performers — 3D */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
          <Card className="chart-3d border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" /> Top Performers</CardTitle>
            </CardHeader>
            <CardContent>
              {data.top_performers.length === 0 ? (
                <p className="text-center py-8 text-sm text-muted-foreground">No data yet</p>
              ) : (
                <div className="space-y-3">
                  {data.top_performers.map((p, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 transition-colors">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-amber-500/20 text-amber-600" : i === 1 ? "bg-slate-400/20 text-slate-500" : "bg-orange-500/20 text-orange-500"}`}>
                        #{i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        <p className="text-[10px] text-muted-foreground">{p.department}</p>
                      </div>
                      <Badge variant="outline" className="text-green-600 text-[10px]">
                        {p.completed} completed
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Overall Completion Radial */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <Card className="border-0 shadow-sm">
          <CardContent className="py-5 px-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                <span className="font-semibold text-sm">Overall Team Learning Progress</span>
              </div>
              <span className="text-2xl font-bold text-primary">{data.completion_rate}%</span>
            </div>
            <Progress value={data.completion_rate} className="h-3" />
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-muted-foreground">{data.completed} of {data.total_enrollments} enrollments completed</p>
              <p className="text-xs text-muted-foreground">{data.team_size} team members</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );

  return (
    <AdminLayout title="Team Analytics" variant={variant}>
      {content}
      {/* Stat Drill-Down Dialog */}
      <Dialog open={!!statDrill} onOpenChange={() => setStatDrill(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {statDrill?.icon && <statDrill.icon className={`w-5 h-5 ${statDrill?.color}`} />}
              {statDrill?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center py-6 bg-muted/20 rounded-xl">
              <p className="text-4xl font-bold">{statDrill?.value}</p>
              <p className="text-sm text-muted-foreground mt-1">{statDrill?.label}</p>
            </div>
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
              <p className="text-sm text-muted-foreground">{statDrill?.detail}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default TeamAnalyticsPage;
